import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

const DEFAULT_PRIZE_LIST = [
  { emoji: '👑', label: 'Winner', amount: '' },
  { emoji: '🥈', label: '2nd Position', amount: '' },
  { emoji: '🥉', label: '3rd Position', amount: '' },
  { emoji: '🏅', label: '4th Position', amount: '' },
  { emoji: '🏅', label: '5th Position', amount: '' },
];

export default function Settings() {
  const [title, setTitle] = useState('');
  const [prize, setPrize] = useState('');
  const [time, setTime] = useState('');
  const [entry, setEntry] = useState('');
  const [perKill, setPerKill] = useState('');
  const [map, setMap] = useState('');
  const [matchType, setMatchType] = useState('');
  const [rules, setRules] = useState('');
  const [prizeList, setPrizeList] = useState(DEFAULT_PRIZE_LIST);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'featured'), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setTitle(d.title ?? '');
        setPrize(d.prize ?? '');
        setTime(d.time ?? '');
        setEntry(d.entry ?? '');
        setPerKill(d.perKill ?? '');
        setMap(d.map ?? '');
        setMatchType(d.matchType ?? '');
        setRules(d.rules ?? '');
        setPrizeList(d.prizeList?.length ? d.prizeList : DEFAULT_PRIZE_LIST);
      }
    });
    return () => unsub();
  }, []);

  const updatePrizeItem = (index, field, value) => {
    const updated = [...prizeList];
    updated[index] = { ...updated[index], [field]: value };
    setPrizeList(updated);
  };

  const addPrizeRow = () => {
    setPrizeList([...prizeList, { emoji: '🏅', label: '', amount: '' }]);
  };

  const removePrizeRow = (index) => {
    setPrizeList(prizeList.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!title.trim()) { alert('Title দাও!'); return; }
    setLoading(true);
    try {
      await setDoc(doc(db, 'settings', 'featured'), {
        title: title.trim(),
        prize: prize.trim(),
        time: time.trim(),
        entry: entry.trim(),
        perKill: perKill.trim(),
        map: map.trim(),
        matchType: matchType.trim(),
        rules: rules.trim(),
        prizeList,
        updatedAt: serverTimestamp(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(false);
  };

  const sidebarLinks = [
    { to: '/', label: '🏠 Dashboard' },
    { to: '/matches', label: '🎯 Matches' },
    { to: '/results', label: '🏆 Results' },
    { to: '/users', label: '👥 Users' },
    { to: '/deposits', label: '💰 Deposits' },
    { to: '/withdrawals', label: '⬆️ Withdrawals' },
    { to: '/settings', label: '⚙️ Settings', active: true },
    { to: '/password-resets', label: '🔑 Password Resets' },
    { to: '/videos', label: '🎬 Videos' }
  ];

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <h1 style={styles.pageTitle}>⚙️ Settings</h1>
        <p style={styles.pageSub}>Featured Tournament সব কিছু এখান থেকে control করো</p>

        {/* Preview */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>👁️ Live Preview</div>
          <div style={styles.previewBanner}>
            <div style={{ color: '#7c3aed', fontSize: 11, fontWeight: 'bold', letterSpacing: 2, marginBottom: 6 }}>🏆 FEATURED</div>
            <div style={{ color: '#fff', fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>{title || 'Tournament Title'}</div>
            <div style={{ color: '#f4c430', fontSize: 22, fontWeight: 'bold', marginBottom: 4 }}>Prize Pool: ৳{prize || '0'}</div>
            <div style={{ color: '#888', fontSize: 13 }}>{time || 'সময়'} • Entry: ৳{entry || '0'}</div>
          </div>
        </div>

        {/* Basic Info */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>📋 Basic Info</div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Tournament Title *</label>
              <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Weekly Grand Tournament" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>সময় / Schedule</label>
              <input style={styles.input} value={time} onChange={e => setTime(e.target.value)} placeholder="শনিবার রাত ৯টা" />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Map</label>
              <input style={styles.input} value={map} onChange={e => setMap(e.target.value)} placeholder="Bermuda" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Match Type</label>
              <input style={styles.input} value={matchType} onChange={e => setMatchType(e.target.value)} placeholder="Solo / Duo / Squad" />
            </div>
          </div>
        </div>

        {/* Prize & Fee */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>💰 Prize & Fee</div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Total Prize Pool (৳)</label>
              <input style={styles.input} value={prize} onChange={e => setPrize(e.target.value)} placeholder="10000" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Entry Fee (৳)</label>
              <input style={styles.input} value={entry} onChange={e => setEntry(e.target.value)} placeholder="200" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Per Kill (৳)</label>
              <input style={styles.input} value={perKill} onChange={e => setPerKill(e.target.value)} placeholder="10" />
            </div>
          </div>
        </div>

        {/* Prize List */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={styles.cardTitle}>🏆 Prize List</div>
            <button style={styles.addBtn} onClick={addPrizeRow}>+ Add Row</button>
          </div>
          {prizeList.map((item, i) => (
            <div key={i} style={styles.prizeRow}>
              <input
                style={{ ...styles.input, width: 50, textAlign: 'center' }}
                value={item.emoji}
                onChange={e => updatePrizeItem(i, 'emoji', e.target.value)}
                placeholder="🥇"
              />
              <input
                style={{ ...styles.input, flex: 1 }}
                value={item.label}
                onChange={e => updatePrizeItem(i, 'label', e.target.value)}
                placeholder="Winner / 2nd Position..."
              />
              <input
                style={{ ...styles.input, width: 100 }}
                value={item.amount}
                onChange={e => updatePrizeItem(i, 'amount', e.target.value)}
                placeholder="৳ Amount"
              />
              <button
                style={styles.removeBtn}
                onClick={() => removePrizeRow(i)}
              >✕</button>
            </div>
          ))}
        </div>

        {/* Rules */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>📜 Rules & Regulations</div>
          <textarea
            style={styles.textarea}
            value={rules}
            onChange={e => setRules(e.target.value)}
            placeholder="এখানে match এর rules লিখো..."
            rows={10}
          />
        </div>

        {/* Save */}
        <button
          style={{ ...styles.saveBtn, opacity: loading ? 0.6 : 1 }}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? '⏳ Save হচ্ছে...' : '💾 সব Save করো'}
        </button>

        {saved && (
          <div style={styles.successMsg}>
            ✅ Successfully saved! App এ real-time update হয়ে গেছে।
          </div>
        )}

        <div style={{ height: 60 }} />
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub: { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#888', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent' },
  navItemActive: { color: '#f4c430', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430', background: '#f4c43011' },
  main: { marginLeft: 240, flex: 1, padding: 32 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub: { color: '#666', fontSize: 14, marginTop: 6, marginBottom: 24 },
  card: {
    background: '#111128', borderRadius: 16,
    border: '1px solid #1e1e3a', padding: 24,
    marginBottom: 20,
  },
  cardTitle: { color: '#fff', fontSize: 16, fontWeight: 'bold', marginBottom: 16 },
  previewBanner: {
    background: '#1a0a2e', borderRadius: 14,
    border: '1px solid #7c3aed55', padding: 20,
  },
  formRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: 200, marginBottom: 16 },
  label: { color: '#888', fontSize: 13, fontWeight: '600', display: 'block', marginBottom: 8 },
  input: {
    background: '#0a0a1a', border: '1px solid #333',
    borderRadius: 10, color: '#fff', fontSize: 14,
    padding: '12px 14px', outline: 'none', boxSizing: 'border-box', width: '100%',
  },
  textarea: {
    width: '100%', background: '#0a0a1a',
    border: '1px solid #333', borderRadius: 10,
    color: '#fff', fontSize: 14, padding: '12px 14px',
    outline: 'none', resize: 'vertical', boxSizing: 'border-box',
    lineHeight: 1.6,
  },
  prizeRow: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 },
  addBtn: {
    background: '#f4c43022', border: '1px solid #f4c43066',
    color: '#f4c430', borderRadius: 8, padding: '8px 14px',
    cursor: 'pointer', fontSize: 13, fontWeight: '600',
  },
  removeBtn: {
    background: '#ff444422', border: '1px solid #ff444444',
    color: '#ff4444', borderRadius: 8, padding: '8px 12px',
    cursor: 'pointer', fontSize: 14,
  },
  saveBtn: {
    background: '#f4c430', border: 'none', borderRadius: 12,
    color: '#000', fontWeight: 'bold', fontSize: 16,
    padding: '16px 40px', cursor: 'pointer', width: '100%',
  },
  successMsg: { color: '#00c896', fontSize: 14, fontWeight: '600', marginTop: 14, textAlign: 'center' },
};